const ENABLE_LOGS = false;

class IaBlogAll extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.articlesPerPage = Number(this.dataset.articlesPerPage);
    this.blogHandles = this.dataset.blogHandles.split(",").filter(Boolean);
    
    this.grid = this.querySelector("[data-ia-blog-grid]");
    this.button = this.querySelector("[data-ia-blog-all-load-more]");
    this.buttonWrap = this.querySelector("[data-ia-blog-all-controls]");
    
    this.cache = [];
    this.fetching = false;
    this.loadedFromCache = 0;
    
    this.log("Initial articles:", this.getRenderedCount());
    
    this.button.addEventListener("click", () => this.loadMore());
    this.fetchAllArticles();
  }

  async fetchAllArticles() {
    if (this.fetching) return;
    this.fetching = true;
    
    this.log("Fetching all articles...");
    
    const allArticles = [];
    
    for (const handle of this.blogHandles) {
      let page = 1;
      let hasMore = true;
      
      while (hasMore) {
        const result = await this.fetchPage(handle, page);
        if (result.articles.length === 0) break;
        
        allArticles.push(...result.articles);
        hasMore = result.hasMore;
        page++;
      }
    }
    
    // Deduplicate and sort
    const seen = new Set();
    const sorted = allArticles
      .filter(a => {
        if (seen.has(a.id)) return false;
        seen.add(a.id);
        return true;
      })
      .sort((a, b) => new Date(b.date) - new Date(a.date));
    
    // Skip articles already rendered by Liquid
    const alreadyRendered = this.getRenderedCount();
    this.cache = sorted.slice(alreadyRendered);
    
    this.log("Fetched:", sorted.length, "total,", this.cache.length, "remaining");
    
    this.fetching = false;
    this.updateButton();
  }

  async fetchPage(handle, page) {
    try {
      const url = `/blogs/${handle}?page=${page}`;
      const res = await fetch(url, { headers: { "X-Requested-With": "XMLHttpRequest" } });
      
      if (!res.ok) return { articles: [], hasMore: false };
      
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");
      const panel = doc.querySelector(`[data-ia-blog-panel][data-blog-handle="${handle}"]`);
      
      if (!panel) return { articles: [], hasMore: false };
      
      // Get ALL articles (both featured and grid items) using universal selector
      const items = panel.querySelectorAll("[data-article-item]");
      const articles = [];
      
      items.forEach(item => {
        if (item.dataset.articleId) {
          // Remove featured class if present, since we're loading into the grid
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = item.outerHTML;
          const clonedItem = tempDiv.firstElementChild;
          clonedItem.classList.remove('ia-featured-article');
          
          articles.push({
            html: clonedItem.outerHTML,
            id: item.dataset.articleId,
            date: item.dataset.articleDate
          });
        }
      });
      
      // Check pagination
      const paginationComponent = panel.querySelector("ia-blog-regular");
      let hasMore = false;
      
      if (paginationComponent) {
        const currentPage = Number(paginationComponent.dataset.currentPage);
        const totalPages = Number(paginationComponent.dataset.totalPages);
        hasMore = currentPage < totalPages;
      }
      
      this.log(`${handle} page ${page}: ${articles.length} articles, hasMore: ${hasMore}`);
      return { articles, hasMore };
      
    } catch (err) {
      console.error("[ia-blog-all] Fetch error:", err);
      return { articles: [], hasMore: false };
    }
  }

  async loadMore() {
    if (this.fetching) {
      this.button.disabled = true;
      while (this.fetching) await new Promise(r => setTimeout(r, 100));
      this.button.disabled = false;
    }
    
    const batch = this.cache.slice(this.loadedFromCache, this.loadedFromCache + this.articlesPerPage);
    
    this.log("Load more:", this.loadedFromCache, "/", this.cache.length, "- batch:", batch.length);
    
    if (batch.length === 0) {
      this.hideButton();
      return;
    }
    
    const fragment = document.createDocumentFragment();
    batch.forEach(article => {
      const div = document.createElement("div");
      div.innerHTML = article.html;
      fragment.appendChild(div.firstElementChild);
    });
    
    this.grid.appendChild(fragment);
    this.loadedFromCache += batch.length;
    
    this.log("Loaded", batch.length, "articles. Total in grid:", this.getRenderedCount());
    
    this.updateButton();
  }

  updateButton() {
    const remaining = this.cache.length - this.loadedFromCache;
    
    if (remaining <= 0) {
      this.hideButton();
    }
  }

  getRenderedCount() {
    // Count all articles (featured + grid items) using universal selector
    return this.querySelectorAll("[data-article-item]").length;
  }

  hideButton() {
    if (this.buttonWrap) this.buttonWrap.style.display = 'none';
  }

  log(...args) {
    if (ENABLE_LOGS) console.log("[ia-blog-all]", ...args);
  }
}

if (!customElements.get("ia-blog-all")) {
  customElements.define("ia-blog-all", IaBlogAll);
}
